/**
 * Jamendo Music-søk for Reisefilmen (in-app musikkoppdagelse).
 *
 * GATING + LISENS (viktig): funksjonen er AV med mindre `VITE_JAMENDO_CLIENT_ID`
 * er satt. Jamendos gratis-API er lisensiert for IKKE-kommersiell bruk; Fricamp
 * er kommersielt (abonnement), og spor som bakes inn i delte videoer krever en
 * Jamendo-lisensavtale (Jamendo Licensing) + som regel kreditering av artisten.
 * Derfor er søket bevisst skjult i UI til en nøkkel er konfigurert OG lisens er
 * avklart. Se also `assets/replay-music/README.md` for det lisenstrygge
 * Pixabay/CC0-alternativet (ferdige spor).
 *
 * CORS: både API-kallet og lyd-strømmen rutes via `contentProxy` (ACAO: *) —
 * Jamendos endepunkter sender ikke nødvendigvis CORS-headere som canvas/WebAudio
 * krever ved kryss-origin.
 */

const CLIENT_ID = (import.meta.env.VITE_JAMENDO_CLIENT_ID as string | undefined) || '';
const CONTENT_PROXY_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/contentProxy';

export function jamendoEnabled(): boolean {
  return CLIENT_ID.length > 0;
}

export interface JamendoTrack {
  id: string;
  title: string;
  artist: string;
  durationSec: number;
  /** Direkte lyd-URL fra Jamendo (mp3). Dekodes via proxiedAudioUrl(). */
  audioUrl: string;
}

/** CORS-ren, proxyet URL for klient-dekoding (WebAudio decodeAudioData). */
export function proxiedAudioUrl(url: string): string {
  return `${CONTENT_PROXY_URL}?url=${encodeURIComponent(url)}`;
}

/**
 * Søker Jamendo etter spor. Kaster hvis ikke konfigurert. Resultatene har en
 * spillbar `audioUrl` (rå Jamendo-URL — bruk proxiedAudioUrl for dekoding).
 */
export async function searchJamendo(query: string, limit = 20): Promise<JamendoTrack[]> {
  if (!jamendoEnabled()) throw new Error('Jamendo er ikke konfigurert (VITE_JAMENDO_CLIENT_ID mangler).');
  const q = query.trim();
  if (!q) return [];
  const api = `https://api.jamendo.com/v3.0/tracks/?client_id=${encodeURIComponent(CLIENT_ID)}`
    + `&format=json&limit=${limit}&audioformat=mp32&order=popularity_total&search=${encodeURIComponent(q)}`;
  const res = await fetch(`${CONTENT_PROXY_URL}?url=${encodeURIComponent(api)}`);
  if (!res.ok) throw new Error(`Jamendo-søk feilet (HTTP ${res.status})`);
  const data = await res.json();
  return (Array.isArray(data?.results) ? data.results : [])
    .map((r: any): JamendoTrack => ({
      id: String(r.id ?? ''),
      title: String(r.name ?? 'Ukjent spor'),
      artist: String(r.artist_name ?? ''),
      durationSec: Number(r.duration) || 0,
      audioUrl: String(r.audio || r.audiodownload || ''),
    }))
    .filter((t: JamendoTrack) => t.audioUrl);
}
